import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AlertCircle, Stethoscope, Building2, Info, Loader2, ShieldCheck } from "lucide-react";
import {
  USER_ID_TYPES, ID_TYPE_GROUPS, getIdTypeMeta,
  validateUserId, formatIdInput, getUserBadge, normaliserId,
} from "../lib/userIdTypes";
import { API } from "../apiBase";

const CARTE = {
  valide: "Carte CPS valide", expiree: "Carte CPS expirée", opposition: "Carte CPS en opposition",
  absente: "Pas de carte CPS physique (e-CPS possible)", inconnue: "État de la carte non vérifié",
};

/**
 * UserIdField — type d'identifiant (formats Maincare) + identifiant.
 * - Un numéro RPPS est vérifié en direct dans l'Annuaire santé ; `onPraticien(data)` reçoit
 *   nom, prénom, profession et état de la carte pour préremplir le formulaire.
 * - `initial` : utilisateur déjà enregistré (son ancien format reste affiché et accepté tel quel).
 */
export function UserIdField({ idType, value, onChange, errorOverride, idPrefix = "userid", onPraticien, initial }) {
  const meta = getIdTypeMeta(idType);
  const badge = getUserBadge(idType);
  const [rpps, setRpps] = useState({ etat: "" });
  const dernier = useRef("");

  const liveError = useMemo(() => {
    if (!value) return null;
    const r = validateUserId(idType, value, { initial });
    return r.ok ? null : r.error;
  }, [idType, value, initial]);
  const shownError = errorOverride || liveError || (rpps.etat === "introuvable" ? rpps.msg : null);

  useEffect(() => {
    const d = normaliserId(idType, value);
    if (idType !== "RPPS" || !/^\d{11}$/.test(d)) { setRpps({ etat: "" }); dernier.current = ""; return undefined; }
    if (initial && initial.id_type === "RPPS" && initial.professional_id === value) return undefined;
    if (dernier.current === d) return undefined;
    dernier.current = d;
    let vivant = true;
    setRpps({ etat: "encours" });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/mss-commande/annuaire/praticien/${d}`, { timeout: 20000 });
        if (!vivant) return;
        setRpps({ etat: "ok", data });
        if (onPraticien) onPraticien(data);
      } catch (ex) {
        if (!vivant) return;
        const st = ex?.response?.status;
        setRpps(st === 404
          ? { etat: "introuvable", msg: "Ce numéro RPPS n'existe pas dans l'Annuaire santé." }
          : { etat: "indispo" });
      }
    }, 350);
    return () => { vivant = false; clearTimeout(t); };
  }, [idType, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTypeChange = (e) => onChange({ id_type: e.target.value, professional_id: "" });
  const onValueChange = (e) => onChange({ id_type: idType, professional_id: formatIdInput(idType, e.target.value) });
  const onBlur = () => {
    const n = normaliserId(idType, value);
    if (n !== value) onChange({ id_type: idType, professional_id: n });
  };

  return (
    <div className="grid gap-3">
      <div>
        <Lbl required>Choisissez votre identifiant</Lbl>
        <select data-testid={`${idPrefix}-type-select`} className="mc-input w-full" value={idType || ""} onChange={onTypeChange}>
          <option value="" disabled>Sélectionner un type…</option>
          {meta?.legacy && <option value={idType}>{meta.label}</option>}
          {Object.entries(ID_TYPE_GROUPS).map(([group, items]) => (
            <optgroup key={group} label={group}>
              {items.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </optgroup>
          ))}
        </select>
        {meta && (
          <p className="mt-1.5 text-[11px] text-[#64748B] inline-flex items-start gap-1">
            <Info size={11} className="mt-0.5 shrink-0" /> {meta.description}
          </p>
        )}
        {badge && (
          <div className="mt-2">
            <span data-testid={`${idPrefix}-badge`}
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border ${
                badge.kind === "soignant"
                  ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                  : "border-sky-200 text-[#0284C7] bg-sky-50"
              }`}>
              {badge.kind === "soignant" ? <Stethoscope size={11} /> : <Building2 size={11} />}
              {badge.label}
            </span>
          </div>
        )}
      </div>

      <div>
        <Lbl required>Identifiant</Lbl>
        <input
          data-testid={`${idPrefix}-value`}
          type={idType === "EMAIL" ? "email" : "text"}
          inputMode={meta?.inputMode || "text"}
          autoComplete="off"
          className="mc-input w-full font-mono"
          value={value || ""}
          onChange={onValueChange}
          onBlur={onBlur}
          placeholder={meta?.placeholder || ""}
          disabled={!idType}
        />
        {shownError ? (
          <p className="mt-1.5 text-[12px] text-red-600 inline-flex items-center gap-1" role="alert">
            <AlertCircle size={12} /> {shownError}
          </p>
        ) : rpps.etat === "encours" ? (
          <p className="mt-1.5 text-[12px] text-[#475569] inline-flex items-center gap-1.5" data-testid={`${idPrefix}-rpps-encours`}>
            <Loader2 size={12} className="animate-spin" /> Vérification dans l'Annuaire santé…
          </p>
        ) : rpps.etat === "ok" ? (
          <p className="mt-1.5 text-[12px] text-emerald-700 inline-flex items-center gap-1.5" data-testid={`${idPrefix}-rpps-ok`}>
            <ShieldCheck size={12} />
            {[rpps.data.nom, rpps.data.prenom].filter(Boolean).join(" ")}
            {rpps.data.profession ? ` · ${rpps.data.profession}` : ""}
            {rpps.data.carte ? ` · ${CARTE[rpps.data.carte] || rpps.data.carte}` : ""}
          </p>
        ) : rpps.etat === "indispo" ? (
          <p className="mt-1.5 text-[12px] text-amber-700">L'Annuaire santé ne répond pas : le numéro sera vérifié par notre équipe.</p>
        ) : null}
      </div>
    </div>
  );
}

function Lbl({ children, required }) {
  return (
    <span className="block text-[11px] uppercase tracking-[0.16em] text-[#475569] mb-2">
      {children}{required && " *"}
    </span>
  );
}

export { USER_ID_TYPES };
